/**
 * Billing ZIP code validation for the SportsPay card form.
 *
 * Kept as a standalone pure module so the rules can be unit tested without
 * rendering the card form, and reused if another payment surface needs them.
 */

/** ISO 3166-1 alpha-2 code for the only country that requires a billing ZIP today. */
export const ZIP_REQUIRED_COUNTRY = 'US'

/** Accepts the 5-digit ZIP and the ZIP+4 extended form. */
const US_ZIP_PATTERN = /^\d{5}(-\d{4})?$/

/**
 * Whether a billing ZIP must be collected for the given country.
 *
 * @param countryCode - ISO 3166-1 alpha-2 code of the selected billing country
 */
export const isZipCodeRequired = (
  countryCode: string | null | undefined
): boolean => countryCode === ZIP_REQUIRED_COUNTRY

/**
 * Validates a billing ZIP for the given country.
 *
 * Returns an error message when invalid, or null when acceptable. Countries
 * that do not require a ZIP always pass, so a stale value left over from a
 * country switch can never block submission.
 *
 * @param value - Raw ZIP input
 * @param countryCode - ISO 3166-1 alpha-2 code of the selected billing country
 */
export const validateZipCode = (
  value: string | null | undefined,
  countryCode: string | null | undefined
): string | null => {
  if (!isZipCodeRequired(countryCode)) {
    return null
  }

  const trimmed = (value ?? '').trim()

  if (!trimmed) {
    return 'ZIP Code Is Required'
  }

  if (!US_ZIP_PATTERN.test(trimmed)) {
    return 'Enter A Valid ZIP Code (12345 Or 12345-6789)'
  }

  return null
}
